import React, { useMemo, useEffect, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath } from 'react-router'
import { useLocation, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import {
  NavDropdown,
  NavDropdownOption,
  SearchBox as SearchBoxComponent
} from 'portal/lib/primitives'
import { useModuleIds, useResponsive } from 'portal/lib/hooks'
import * as ROUTES from 'portal/routes'
import { trackSwitcherSelect } from './trackSwitcherSelect'
import { trackSwitcherOpen } from './trackSwitcherOpen'
import { selectProjectsForSwitcherFactory } from './selectProjectsForSwitcherFactory'
import { projectsGetSaga } from 'portal/store/projects/actions'

// Type definition for the JavaScript SearchBox component
const SearchBox = SearchBoxComponent as React.FC<{
  'value': string
  'onChange': (value: string) => void
  'placeholder'?: string
  'data-testid'?: string
}>

interface ProjectItem {
  key: string
  label: string
  enabled: boolean
  icon: null
  country?: string
  selected: boolean
  uri?: string
  moduleName: string
}

interface ProjectSwitcherProps {
  'className'?: string
  'data-testid'?: string
}

export function ProjectSwitcher(props: ProjectSwitcherProps) {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()
  const [open, setOpen] = useState(false)
  const [useFlags, setUseFlags] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { isMobile } = useResponsive()

  const { projects } = useSelector(
    useMemo(() => selectProjectsForSwitcherFactory(moduleName), [moduleName])
  )

  useEffect(() => {
    if (accountId) {
      dispatch(projectsGetSaga(accountId))
    }
  }, [accountId, dispatch])

  useEffect(() => {
    // Loop through projects to see if more than 1 country exists, if so, enable useFlags
    const countrySet = new Set<string>()
    projects.forEach(project => {
      if (project.country) {
        countrySet.add(project.country)
      }
    })
    if (countrySet.size > 1) {
      setUseFlags(true)
    } else {
      setUseFlags(false)
    }
  }, [projects])

  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()

  const items = useMemo(() => {
    const append = pathname.endsWith('/events') ? '/events' : ''

    const search = searchParams.toString()

    const dynamicRoute = (): string => {
      const currentURL = pathname.split('/')
      switch (currentURL[currentURL.length - 1]) {
        case 'home':
          return ROUTES.HOMEPAGE
        case 'readings':
          return ROUTES.UTILITIES_READINGS
        case 'meters':
          return ROUTES.UTILITIES_METERS
        case 'material-certifications':
          return ROUTES.ALERTS_MATERIAL_CERTIFICATIONS_TABLE
        case 'quality-assurance':
          return ROUTES.ALERTS_QUALITY_ASSURANCE_TABLE
        case 'submission-issues':
          return ROUTES.ALERTS_SUBMISSION_ISSUES_TABLE
        case 'waste-compliance':
          return ROUTES.ALERTS_WASTE_COMPLIANCE_TABLE
        case 'settings':
          return ROUTES.PROJECTSETTINGS
        case 'design-matches':
          return ROUTES.DELIVERY_DESIGN_MATCHES
        default:
          return ROUTES.BASE_MODULE_NAV
      }
    }

    return projects
      .map(
        ({ key, label, country, moduleName }): ProjectItem => ({
          key,
          label,
          enabled: true,
          icon: null,
          country: country,
          selected: key === projectId,
          moduleName,
          uri:
            accountId &&
            `${generatePath(dynamicRoute(), {
              accountId,
              projectId: key,
              moduleName
            })}${append}${search ? `?${search}` : ''}`
        })
      )
      .sort((a, b) =>
        a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })
      )
  }, [accountId, pathname, projectId, projects, searchParams])

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items

    const lowerQuery = searchQuery.toLowerCase()
    return items.filter(item => item.label.toLowerCase().includes(lowerQuery))
  }, [items, searchQuery])

  // Clear search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
    }
  }, [open])

  const [selectedItemKey, selectedItem] = useMemo(() => {
    const selectedItem =
      items.find(item => item.selected) || ({} as ProjectItem)
    const selectedModuleKey = selectedItem.key

    return [selectedModuleKey, selectedItem]
  }, [items])

  const handleSetOpen = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)

      if (nextOpen && !open) {
        dispatch(trackSwitcherOpen(selectedItemKey))
      }
    },
    [dispatch, open, selectedItemKey]
  )

  const handleClick = useCallback(
    (item: ProjectItem) => {
      setOpen(false)
      setTimeout(() => {
        dispatch(
          trackSwitcherSelect(selectedItemKey, item.key, accountId, projectId)
        )
      }, 0)
    },
    [accountId, dispatch, projectId, selectedItemKey]
  )

  const loaded = !!projectId && !!selectedItemKey

  if (!projectId) {
    return <Container {...props} />
  }

  return (
    <Container {...props} data-testid="project-switcher">
      <NavDropdown
        id="ProjectNavDropdown"
        open={open}
        setOpen={handleSetOpen}
        label={isMobile ? 'Project' : selectedItem.label}
        title={selectedItem.label}
        disabled={items.length <= 1}
        loading={!loaded}
      >
        {items.length > 5 && (
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search projects..."
            data-testid="project-search-input"
          />
        )}
        {filteredItems.length > 0 ? (
          filteredItems.map(item => (
            <NavDropdownOption
              id={`NavigateTo_${item.label}`}
              data-testid={`NavigateTo_${item.label}`}
              key={item.key}
              Icon={item.icon}
              label={item.label}
              uri={item.uri}
              useFlags={useFlags}
              country={item.country}
              onClick={handleClick}
              onClickPassbackValue={item}
              selected={item.selected}
            />
          ))
        ) : (
          <NoResultsMessage>No projects found</NoResultsMessage>
        )}
      </NavDropdown>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex: 0 0 auto;
`

const NoResultsMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colours?.GREY_DARK || '#666'};
  font-style: italic;
`
